import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

function useScrollAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return { ref, isVisible };
}

function AnimatedSection({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        transitionDelay: `${delay}ms`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
      }}
    >
      {children}
    </div>
  );
}

function StaggerChild({
  children,
  index = 0,
}: {
  children: React.ReactNode;
  index?: number;
}) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref}
      className="transition-all duration-600 ease-out"
      style={{
        transitionDelay: `${index * 120}ms`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? 'translateY(0) scale(1)'
          : 'translateY(30px) scale(0.97)',
      }}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prevSlide, setPrevSlide] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const heroImages = [
    'https://public.readdy.ai/ai/img_res/cf979f391c015c6dfa6041d24933895e.jpg',
    'https://readdy.ai/api/search-image?query=pristine-modern-japanese-hospital-corridor-long-hallway-with-polished-white-floors-soft-recessed-ceiling-lights-clean-walls-with-subtle-wood-panel-accents-no-people-empty-hallway-sterile-and-immaculate-environment-natural-light-from-side-windows-creating-peaceful-calming-medical-atmosphere-high-end-japanese-healthcare-facility-architecture&width=1920&height=1080&seq=hero-jp-corridor-002&orientation=landscape',
    'https://readdy.ai/api/search-image?query=modern-japanese-hospital-pharmacy-dispensary-room-interior-with-organized-medicine-shelves-automated-dispensing-equipment-clean-white-counters-bright-fluorescent-lighting-no-people-empty-room-sterile-organized-pharmaceutical-storage-typical-japanese-medical-facility-precision-and-order-high-tech-medical-equipment&width=1920&height=1080&seq=hero-jp-pharmacy-003&orientation=landscape',
    'https://readdy.ai/api/search-image?query=elegant-modern-japanese-hospital-reception-lobby-interior-with-curved-white-reception-desk-warm-indirect-lighting-natural-wood-accents-on-walls-potted-green-plants-no-people-empty-spacious-lobby-clean-minimalist-japanese-architectural-design-large-windows-with-soft-natural-light-premium-healthcare-facility-entrance&width=1920&height=1080&seq=hero-jp-lobby-004&orientation=landscape',
  ];

  /* ---------- Scroll & Resize Effects ---------- */
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setPrevSlide(currentSlide);
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [currentSlide, heroImages.length]);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleMenuLinkClick = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <style>{`
        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.12); }
        }
        .slide-active {
          animation: kenBurns 6s ease-out forwards;
          opacity: 1;
          transition: opacity 1.8s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .slide-exit {
          opacity: 0;
          transition: opacity 1.8s cubic-bezier(0.4, 0, 0.2, 1);
          transform: scale(1.08);
        }
        .slide-hidden {
          opacity: 0;
        }
        @keyframes countUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .mobile-menu-enter {
          max-height: 0;
          opacity: 0;
          overflow: hidden;
          transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
        }
        .mobile-menu-open {
          max-height: 500px;
          opacity: 1;
        }
      `}</style>

      {/* ---------- Navigation ---------- */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || mobileMenuOpen ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/fd436d323a5301b445606f48305b679c/54964fd3d2370d0b9e82c749d804b378.png"
                alt="株式会社Velia"
                className="h-9 md:h-12 w-auto"
              />
            </div>

            <div className="hidden md:flex items-center gap-4 lg:gap-8">
              <a
                href="#home"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer min-h-[44px] flex items-center ${
                  isScrolled
                    ? 'text-gray-700 hover:text-teal-600'
                    : 'text-white hover:text-teal-300'
                }`}
              >
                ホーム
              </a>
              <a
                href="#about"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer min-h-[44px] flex items-center ${
                  isScrolled
                    ? 'text-gray-700 hover:text-teal-600'
                    : 'text-white hover:text-teal-300'
                }`}
              >
                会社概要
              </a>
              <a
                href="#service"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer min-h-[44px] flex items-center ${
                  isScrolled
                    ? 'text-gray-700 hover:text-teal-600'
                    : 'text-white hover:text-teal-300'
                }`}
              >
                オンライン診療
              </a>
              <a
                href="#development"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer min-h-[44px] flex items-center ${
                  isScrolled
                    ? 'text-gray-700 hover:text-teal-600'
                    : 'text-white hover:text-teal-300'
                }`}
              >
                システム開発
              </a>
              <a
                href="#partners"
                className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer min-h-[44px] flex items-center ${
                  isScrolled
                    ? 'text-gray-700 hover:text-teal-600'
                    : 'text-white hover:text-teal-300'
                }`}
              >
                パートナー
              </a>
              <a
                href="#contact"
                className="bg-teal-600 text-white px-5 lg:px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer min-h-[44px] flex items-center justify-center"
              >
                お問い合わせ
              </a>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-11 h-11 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
                isScrolled || mobileMenuOpen ? 'text-gray-700' : 'text-white'
              }`}
              aria-label="メニュー"
            >
              <i
                className={`${
                  mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                } text-2xl`}
              ></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden bg-white shadow-lg mobile-menu-enter ${
            mobileMenuOpen ? 'mobile-menu-open' : ''
          }`}
        >
          <div className="px-4 py-4 space-y-1">
            <a
              href="#home"
              onClick={handleMenuLinkClick}
              className="block text-gray-700 hover:text-teal-600 hover:bg-teal-50 px-4 py-3 rounded-lg cursor-pointer min-h-[44px] text-base font-medium transition-colors"
            >
              ホーム
            </a>
            <a
              href="#about"
              onClick={handleMenuLinkClick}
              className="block text-gray-700 hover:text-teal-600 hover:bg-teal-50 px-4 py-3 rounded-lg cursor-pointer min-h-[44px] text-base font-medium transition-colors"
            >
              会社概要
            </a>
            <a
              href="#service"
              onClick={handleMenuLinkClick}
              className="block text-gray-700 hover:text-teal-600 hover:bg-teal-50 px-4 py-3 rounded-lg cursor-pointer min-h-[44px] text-base font-medium transition-colors"
            >
              オンライン診療サービス
            </a>
            <a
              href="#development"
              onClick={handleMenuLinkClick}
              className="block text-gray-700 hover:text-teal-600 hover:bg-teal-50 px-4 py-3 rounded-lg cursor-pointer min-h-[44px] text-base font-medium transition-colors"
            >
              システム開発
            </a>
            <a
              href="#partners"
              onClick={handleMenuLinkClick}
              className="block text-gray-700 hover:text-teal-600 hover:bg-teal-50 px-4 py-3 rounded-lg cursor-pointer min-h-[44px] text-base font-medium transition-colors"
            >
              パートナー医療法人
            </a>
            <a
              href="#contact"
              onClick={handleMenuLinkClick}
              className="block bg-teal-600 text-white text-center py-3 rounded-lg font-medium cursor-pointer min-h-[44px] text-base mt-2 hover:bg-teal-700 transition-colors"
            >
              お問い合わせ
            </a>
          </div>
        </div>
      </nav>

      {/* ---------- Hero Section ---------- */}
      <section
        id="home"
        className="relative min-h-[100svh] flex items-center overflow-hidden"
      >
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 ${
                index === currentSlide
                  ? 'slide-active z-10'
                  : index === prevSlide
                  ? 'slide-exit z-[5]'
                  : 'slide-hidden z-0'
              }`}
            >
              <img
                src={image}
                alt={`Medical Facility ${index + 1}`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/65 to-slate-900/30 md:to-transparent z-20"></div>
        </div>

        <div className="relative w-full max-w-[1200px] mx-auto px-4 sm:px-6 pt-24 pb-20 md:py-32 z-30">
          <div className="max-w-3xl">
            <AnimatedSection delay={200}>
              <div className="inline-block mb-4 md:mb-6">
                <span className="px-3 py-1.5 md:px-4 md:py-2 border border-teal-400 text-teal-300 text-xs md:text-sm font-medium rounded-full">
                  医療×テクノロジー
                </span>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={400}>
              <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-bold text-white mb-4 md:mb-6 leading-tight">
                医療の未来を、<br />テクノロジーで支える
              </h1>
            </AnimatedSection>

            <AnimatedSection delay={600}>
              <p className="text-base sm:text-lg md:text-xl text-gray-200 mb-8 md:mb-10">
                オンライン診療プラットフォーム「ニアクリ」の開発・運営
              </p>
            </AnimatedSection>

            <AnimatedSection delay={800}>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <a
                  href="https://www.near-clinic.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-teal-600 text-white px-6 md:px-8 py-3.5 md:py-4 rounded-lg text-sm md:text-base font-medium hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer text-center min-h-[44px] flex items-center justify-center"
                >
                  ニアクリを見る
                </a>
                <a
                  href="#contact"
                  className="bg-white/10 backdrop-blur-sm text-white border-2 border-white px-6 md:px-8 py-3.5 md:py-4 rounded-lg text-sm md:text-base font-medium hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer text-center min-h-[44px] flex items-center justify-center"
                >
                  お問い合わせ
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>

        <div className="absolute bottom-8 md:bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce z-30">
          <i className="ri-arrow-down-line text-2xl md:text-3xl text-white"></i>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 md:bottom-10 right-4 md:right-10 flex space-x-2 z-30">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setPrevSlide(currentSlide);
                setCurrentSlide(index);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-500 cursor-pointer min-w-[8px] min-h-[8px] ${
                index === currentSlide
                  ? 'bg-white w-6 md:w-8'
                  : 'bg-white/50'
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* ---------- Strengths Section ---------- */}
      <section className="py-16 md:py-20 lg:py-24 bg-gray-50 relative">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'radial-gradient(circle, #0d9488 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        ></div>
        <div className="relative w-full max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-12 mb-10 md:mb-16">
            <AnimatedSection>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
                私たちの強み
              </h2>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                システム開発と医療の専門知識を融合させた、<br className="hidden lg:block" />
                信頼性の高いオンライン診療サービスを提供します
              </p>
            </AnimatedSection>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            <StaggerChild index={0}>
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 md:p-8 rounded-2xl h-full">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-teal-600 rounded-xl flex items-center justify-center mb-4 md:mb-6">
                  <i className="ri-code-box-line text-2xl md:text-3xl text-white"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                  自社開発システム
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  医療現場のニーズに応える、柔軟で安全性の高いシステムを自社で開発・運用しています
                </p>
              </div>
            </StaggerChild>

            <StaggerChild index={1}>
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow h-full">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-700 rounded-xl flex items-center justify-center mb-4 md:mb-6">
                  <i className="ri-team-line text-2xl md:text-3xl text-white"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                  医療専門家との連携
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  提携医療法人との緊密な協力体制により、医療現場の実情に即したサービスを実現
                </p>
              </div>
            </StaggerChild>

            <StaggerChild index={2}>
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow h-full">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-slate-700 rounded-xl flex items-center justify-center mb-4 md:mb-6">
                  <i className="ri-user-star-line text-2xl md:text-3xl text-white"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                  プロフェッショナルチーム
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  システムエンジニアと医療従事者が協働し、最高品質のサービスを提供
                </p>
              </div>
            </StaggerChild>
          </div>
        </div>
      </section>

      {/* ---------- Service Section ---------- */}
      <section id="service" className="py-16 md:py-20 lg:py-24 bg-white relative">
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              'linear-gradient(#0d9488 1px, transparent 1px), linear-gradient(90deg, #0d9488 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        ></div>
        <div className="relative w-full max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
            <AnimatedSection>
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-6 md:mb-8 leading-tight">
                  オンライン診療<br />
                  プラットフォーム<br />
                  「ニアクリ」
                </h2>
                <div className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                  {[
                    '患者様と医師をつなぐ安全で使いやすいビデオ診療システム',
                    '電子カルテ連携による効率的な診療記録管理',
                    'オンライン決済・処方箋発行の一元管理',
                    '24時間365日の安定稼働とセキュリティ対策',
                  ].map((txt, i) => (
                    <div key={i} className="flex items-start">
                      <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-xl md:text-2xl text-teal-600"></i>
                      </div>
                      <p className="text-sm md:text-lg text-gray-700 ml-2 md:ml-3">{txt}</p>
                    </div>
                  ))}
                </div>
                <a
                  href="https://www.near-clinic.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-teal-600 font-medium hover:text-teal-700 cursor-pointer min-h-[44px]"
                >
                  ニアクリを見る <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={300}>
              <div className="relative">
                <div className="relative z-10">
                  <img
                    src="https://static.readdy.ai/image/fd436d323a5301b445606f48305b679c/75c4ee6986dc06a4b888c9c7962cac4e.png"
                    alt="ニアクリ プラットフォーム"
                    className="w-full h-auto max-w-full rounded-2xl shadow-2xl"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 md:-bottom-10 md:-right-10 w-40 h-40 md:w-64 md:h-64 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full opacity-20 blur-3xl"></div>
                <div className="absolute -top-6 -left-6 md:-top-10 md:-left-10 w-32 h-32 md:w-48 md:h-48 bg-gradient-to-br from-teal-300 to-cyan-300 rounded-full opacity-20 blur-3xl"></div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ---------- Development Section ---------- */}
      <section id="development" className="py-16 md:py-20 lg:py-24 bg-gray-50 relative">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'radial-gradient(circle, #0d9488 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        ></div>
        <div className="relative w-full max-w-[1200px] mx-auto px-4 sm:px-6">
          <AnimatedSection>
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                システム開発
              </h2>
              <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
                医療現場の課題を深く理解し、最新技術を活用した自社開発システムで、安全で効率的なオンライン診療を実現します
              </p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
            <StaggerChild index={0}>
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg h-full">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 md:mb-6">
                  <i className="ri-shield-check-line text-xl md:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                  高度なセキュリティ
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-3 md:mb-4">
                  医療情報を扱うシステムとして、最高水準のセキュリティ対策を実装。個人情報保護法、医療法に完全準拠した安全な環境を提供します。
                </p>
                <ul className="space-y-2">
                  {[
                    'エンドツーエンド暗号化通信',
                    '多要素認証システム',
                    '定期的なセキュリティ監査',
                  ].map((item) => (
                    <li key={item} className="flex items-center text-xs md:text-sm text-gray-600">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-2 shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </StaggerChild>

            <StaggerChild index={1}>
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg h-full">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 md:mb-6">
                  <i className="ri-speed-line text-xl md:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                  高速・安定稼働
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-3 md:mb-4">
                  クラウドインフラを活用した高可用性アーキテクチャにより、24時間365日の安定したサービス提供を実現しています。
                </p>
                <ul className="space-y-2">
                  {[
                    '99.9%以上の稼働率保証',
                    '自動スケーリング対応',
                    'リアルタイム監視体制',
                  ].map((item) => (
                    <li key={item} className="flex items-center text-xs md:text-sm text-gray-600">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-2 shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </StaggerChild>

            <StaggerChild index={2}>
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg h-full">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 md:mb-6">
                  <i className="ri-smartphone-line text-xl md:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                  マルチデバイス対応
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-3 md:mb-4">
                  スマートフォン、タブレット、PCなど、あらゆるデバイスで快適に利用できるレスポンシブデザインを採用しています。
                </p>
                <ul className="space-y-2">
                  {[
                    'iOS / Android ネイティブアプリ',
                    'Webブラウザ対応',
                    '直感的なUI/UX設計',
                  ].map((item) => (
                    <li key={item} className="flex items-center text-xs md:text-sm text-gray-600">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-2 shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </StaggerChild>

            <StaggerChild index={3}>
              <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg h-full">
                <div className="w-12 h-12 md:w-14 md:h-14 bg-teal-100 rounded-lg flex items-center justify-center mb-4 md:mb-6">
                  <i className="ri-customer-service-2-line text-xl md:text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 md:mb-4">
                  充実のサポート体制
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-3 md:mb-4">
                  導入から運用まで、専門スタッフが丁寧にサポート。医療現場の声を反映した継続的な機能改善を行っています。
                </p>
                <ul className="space-y-2">
                  {[
                    '導入時の研修プログラム',
                    '専任サポート担当者配置',
                    '定期的なアップデート提供',
                  ].map((item) => (
                    <li key={item} className="flex items-center text-xs md:text-sm text-gray-600">
                      <i className="ri-checkbox-circle-fill text-teal-600 mr-2 shrink-0"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </StaggerChild>
          </div>
        </div>
      </section>

      {/* ---------- Partners Section ---------- */}
      <section id="partners" className="py-16 md:py-20 lg:py-24 bg-white relative">
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              'linear-gradient(#0d9488 1px, transparent 1px), linear-gradient(90deg, #0d9488 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        ></div>
        <div className="relative w-full max-w-[1200px] mx-auto px-4 sm:px-6">
          <AnimatedSection>
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                パートナー医療法人
              </h2>
              <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
                信頼できる医療法人との提携により、質の高いオンライン診療サービスを提供しています
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-12 lg:p-16 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 md:w-64 h-40 md:h-64 bg-teal-500 rounded-full opacity-10 blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-32 md:w-48 h-32 md:h-48 bg-cyan-500 rounded-full opacity-10 blur-3xl"></div>

              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
                <div className="order-2 md:order-1">
                  <div className="text-teal-400 text-4xl md:text-6xl mb-4 md:mb-6">
                    &ldquo;
                  </div>
                  <p className="text-white text-base md:text-xl leading-relaxed mb-6 md:mb-8">
                    訪問診療から外来まで様々な分野に対応する医療法人で、院内DX化やシステムを使った新たな医療体制構築に尽力。より便利な医療を提供するためのオンラインクリニック【ニアクリ】の開発から運営まで多岐にわたる活躍
                  </p>
                  <div className="text-gray-300">
                    <p className="font-bold text-base md:text-lg text-white mb-1">
                      医療法人良樹会
                    </p>
                  </div>
                </div>

                <div className="relative order-1 md:order-2">
                  <div className="w-full aspect-[6/7] rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=modern-beautiful-japanese-hospital-building-exterior-architecture-clean-white-facade-with-large-glass-windows-contemporary-medical-facility-design-well-maintained-landscaping-with-green-plants-blue-sky-background-professional-healthcare-building-in-japan-daytime-natural-lighting-welcoming-entrance-area-typical-japanese-medical-center-architecture&width=600&height=700&seq=partner-jp-hospital-exterior-001&orientation=portrait"
                      alt="パートナー医療法人"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <div className="mt-10 md:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8">
            <StaggerChild index={0}>
              <div className="text-center p-6 md:p-8 bg-gray-50 rounded-2xl">
                <div className="text-3xl md:text-4xl font-bold text-teal-600 mb-1 md:mb-2">
                  50+
                </div>
                <p className="text-sm md:text-base text-gray-600 font-medium">
                  提携医療機関
                </p>
              </div>
            </StaggerChild>
            <StaggerChild index={1}>
              <div className="text-center p-6 md:p-8 bg-gray-50 rounded-2xl">
                <div className="text-3xl md:text-4xl font-bold text-teal-600 mb-1 md:mb-2">
                  10,000+
                </div>
                <p className="text-sm md:text-base text-gray-600 font-medium">
                  診療件数
                </p>
              </div>
            </StaggerChild>
            <StaggerChild index={2}>
              <div className="text-center p-6 md:p-8 bg-gray-50 rounded-2xl">
                <div className="text-3xl md:text-4xl font-bold text-teal-600 mb-1 md:mb-2">
                  98%
                </div>
                <p className="text-sm md:text-base text-gray-600 font-medium">
                  患者満足度
                </p>
              </div>
            </StaggerChild>
          </div>
        </div>
      </section>

      {/* ---------- About Section ---------- */}
      <section id="about" className="py-16 md:py-20 lg:py-24 bg-gray-50 relative">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'radial-gradient(circle, #0d9488 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        ></div>
        <div className="relative w-full max-w-[1200px] mx-auto px-4 sm:px-6">
          <AnimatedSection>
            <div className="text-center mb-10 md:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 md:mb-6">
                会社概要
              </h2>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-6 sm:p-8 md:p-12 max-w-4xl mx-auto">
              <div className="space-y-6 md:space-y-8">
                <div className="flex flex-col md:grid md:grid-cols-3 gap-2 md:gap-6 pb-6 md:pb-8 border-b border-gray-200">
                  <div className="font-bold text-gray-700 text-sm md:text-base">
                    会社名
                  </div>
                  <div className="md:col-span-2 text-gray-900 text-sm md:text-base">
                    株式会社Velia
                  </div>
                </div>

                <div className="flex flex-col md:grid md:grid-cols-3 gap-2 md:gap-6 pb-6 md:pb-8 border-b border-gray-200">
                  <div className="font-bold text-gray-700 text-sm md:text-base">
                    所在地
                  </div>
                  <div className="md:col-span-2 text-gray-900 text-sm md:text-base">
                    〒532-0011
                    <br />
                    大阪府大阪市淀川区西中島4丁目2番6号
                    <br />
                    NLC新大阪2号館302号
                  </div>
                </div>

                <div className="flex flex-col md:grid md:grid-cols-3 gap-2 md:gap-6">
                  <div className="font-bold text-gray-700 text-sm md:text-base">
                    事業内容
                  </div>
                  <div className="md:col-span-2 text-gray-900 text-sm md:text-base">
                    <ul className="space-y-2">
                      <li>• オンライン診療プラットフォーム「ニアクリ」の開発・運営</li>
                      <li>• 医療機関向けシステム開発</li>
                      <li>• 医療法人との提携事業</li>
                      <li>• 医療DXコンサルティング</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ---------- Contact CTA ---------- */}
      <section id="contact" className="relative py-20 md:py-28 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern-japanese-office-workspace-with-clean-minimalist-design-natural-sunlight-through-large-windows-typical-japanese-business-environment-with-warm-wood-tones-and-organized-desk-layout-professional-atmosphere-light-neutral-colors-creating-productive-workspace&width=1920&height=800&seq=cta-jp-bg-001&orientation=landscape"
            alt="Contact Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-slate-900/85"></div>
        </div>

        <div className="relative w-full max-w-[1000px] mx-auto px-4 sm:px-6 text-center">
          <AnimatedSection>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
              医療の未来を一緒に創りませんか
            </h2>
          </AnimatedSection>
          <AnimatedSection delay={200}>
            <p className="text-base md:text-xl text-gray-200 mb-8 md:mb-12">
              オンライン診療システムの導入や
              <br />
              提携に関するご相談を承ります
            </p>
          </AnimatedSection>
          <AnimatedSection delay={400}>
            <a
              href="#contact-form"
              className="inline-flex items-center justify-center bg-teal-600 text-white px-6 sm:px-8 md:px-10 py-4 md:py-5 rounded-full text-base md:text-lg font-medium hover:bg-teal-700 transition-all shadow-2xl whitespace-nowrap cursor-pointer min-h-[44px] w-full sm:w-auto"
            >
              <span className="w-8 h-8 md:w-10 md:h-10 bg-teal-500 rounded-full flex items-center justify-center mr-3 md:mr-4 shrink-0">
                <i className="ri-mail-line text-lg md:text-xl"></i>
              </span>
              お問い合わせ
              <i className="ri-arrow-right-up-line text-xl md:text-2xl ml-2 md:ml-3"></i>
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* ---------- Contact Form ---------- */}
      <section className="py-16 md:py-20 lg:py-24 bg-white relative">
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              'linear-gradient(#0d9488 1px, transparent 1px), linear-gradient(90deg, #0d9488 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        ></div>
        <div className="relative w-full max-w-[720px] mx-auto px-4 sm:px-6">
          <AnimatedSection>
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl sm: text-3xl font-bold text-gray-900 mb-3 md:mb-4">
                お問い合わせフォーム
              </h2>
              <p className="text-sm md:text-base text-gray-600">
                以下のフォームよりお気軽にお問い合わせください
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={200}>
            {formStatus === 'success' ? (
              <div className="bg-teal-50 border border-teal-200 rounded-2xl p-10 text-center">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">送信が完了しました</h3>
                <p className="text-gray-600 text-sm md:text-base">
                  お問い合わせありがとうございます。<br />
                  担当者より折り返しご連絡いたします。
                </p>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form
                className="space-y-5 md:space-y-6 bg-gray-50 p-5 sm:p-6 md:p-8 rounded-2xl"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (formStatus === 'submitting') return;
                  const form = e.currentTarget;
                  const textarea = form.querySelector('textarea');
                  if (textarea && textarea.value.length > 500) {
                    return;
                  }
                  setFormStatus('submitting');
                  try {
                    const data = new URLSearchParams(new FormData(form) as any);
                    const res = await fetch('https://readdy.ai/api/form/d69s5dtt1k61917cme50', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: data.toString(),
                    });
                    if (res.ok) {
                      setFormStatus('success');
                    } else {
                      setFormStatus('error');
                    }
                  } catch {
                    setFormStatus('error');
                  }
                }}
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-3 md:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 md:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                  />
                </div>

                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    医療機関名・組織名
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    className="w-full px-3 md:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-3 md:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm md:text-base min-h-[44px]"
                  />
                </div>

                <div>
                  <label htmlFor="inquiry-type" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    お問い合わせ種別 <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="inquiry-type"
                    name="inquiry-type"
                    required
                    className="w-full px-3 md:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer text-sm md:text-base min-h-[44px]"
                  >
                    <option value="">選択してください</option>
                    <option value="service">サービスについて</option>
                    <option value="partnership">提携について</option>
                    <option value="system">システム開発について</option>
                    <option value="other">その他</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-3 md:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none text-sm md:text-base"
                  ></textarea>
                  <p className="text-xs md:text-sm text-gray-500 mt-1">500文字以内でご入力ください</p>
                </div>

                {formStatus === 'error' && (
                  <p className="text-red-500 text-sm text-center">
                    送信に失敗しました。時間をおいて再度お試しください。
                  </p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-teal-600 text-white py-3.5 md:py-4 rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base min-h-[44px] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? '送信中...' : '送信する'}
                </button>
              </form>
            )}
          </AnimatedSection>
        </div>
      </section>

      {/* ---------- Footer ---------- */}
      <footer className="bg-slate-800 text-white pt-12 md:pt-16 lg:pt-20 pb-6 md:pb-8">
        <div className="w-full max-w-[1200px] mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 lg:gap-12 mb-10 md:mb-16">
            <div className="sm:col-span-2 lg:col-span-1">
              <img
                src="https://static.readdy.ai/image/fd436d323a5301b445606f48305b679c/54964fd3d2370d0b9e82c749d804b378.png"
                alt="株式会社Velia"
                className="h-10 md:h-12 w-auto mb-4 md:mb-6"
              />
              <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                医療とテクノロジーを融合し、<br />
                より良い医療サービスの実現を目指します
              </p>
            </div>

            <div>
              <h4 className="text-xs md:text-sm font-bold uppercase mb-4 md:mb-6 text-gray-300">
                サービス
              </h4>
              <ul className="space-y-2 md:space-y-3">
                <li>
                  <a
                    href="https://www.near-clinic.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors text-xs md:text-sm cursor-pointer min-h-[44px] inline-flex items-center"
                  >
                    ニアクリ
                  </a>
                </li>
                <li>
                  <a
                    href="#development"
                    className="text-gray-400 hover:text-white transition-colors text-xs md:text-sm cursor-pointer min-h-[44px] inline-flex items-center"
                  >
                    システム開発
                  </a>
                </li>
                <li>
                  <a
                    href="#partners"
                    className="text-gray-400 hover:text-white transition-colors text-xs md:text-sm cursor-pointer min-h-[44px] inline-flex items-center"
                  >
                    導入事例
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs md:text-sm font-bold uppercase mb-4 md:mb-6 text-gray-300">
                企業情報
              </h4>
              <ul className="space-y-2 md:space-y-3">
                <li>
                  <a
                    href="#about"
                    className="text-gray-400 hover:text-white transition-colors text-xs md:text-sm cursor-pointer min-h-[44px] inline-flex items-center"
                  >
                    会社概要
                  </a>
                </li>
                <li>
                  <a
                    href="#partners"
                    className="text-gray-400 hover:text-white transition-colors text-xs md:text-sm cursor-pointer min-h-[44px] inline-flex items-center"
                  >
                    パートナー医療法人
                  </a>
                </li>
                <li>
                  <a
                    href="#contact"
                    className="text-gray-400 hover:text-white transition-colors text-xs md:text-sm cursor-pointer min-h-[44px] inline-flex items-center"
                  >
                    お問い合わせ
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs md:text-sm font-bold uppercase mb-4 md:mb-6 text-gray-300">
                所在地
              </h4>
              <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                〒532-0011<br />
                大阪府大阪市淀川区西中島<br />
                4丁目2番6号<br />
                NLC新大阪2号館302号
              </p>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-6 md:pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
              <p className="text-gray-400 text-xs md:text-sm">
                &copy; 2024 株式会社Velia. All rights reserved.
              </p>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white text-xs md:text-sm transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
